import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Settings2, ChevronDown, ChevronRight, Download, Trash2, GripVertical, Star, X } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select } from '../../components/ui/select';
import { Pagination } from '../../components/ui/Pagination';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { useTickets, useTicketSlaBatch, useDeleteTicket } from '../../hooks/use-tickets';
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, ALL_STATUSES, ALL_PRIORITIES, formatTicketId } from '../../lib/constants';
import { formatDate, formatDateTime } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import type { Ticket } from '../../types';
import type { TicketSla } from '../../types';

// ── SLA timer helpers ──────────────────────────────────

function formatSlaRemaining(deadline: string | null, met: boolean | null, isPaused: boolean, limitMinutes: number | null, justification?: string | null, isOutsideBusinessHours?: boolean): React.ReactNode {
  if (!deadline || limitMinutes === null) return <span className="text-gray-400 dark:text-dark-muted">—</span>;
  if (met === true) return <span className="text-green-600 text-xs font-medium">✓ Respectat</span>;
  if (met === false && justification) return (
    <span className="text-amber-600 text-xs font-medium">✓ Justificat</span>
  );
  if (met === false) return (
    <span className="text-red-600 text-xs font-medium">✗ Depășit (limita: {formatMinutes(limitMinutes)})</span>
  );
  // Still running or paused
  const remainMs = new Date(deadline).getTime() - Date.now();
  if (remainMs <= 0) return <span className="text-red-600 text-xs font-medium">✗ Depășit (limita: {formatMinutes(limitMinutes)})</span>;
  const label = formatMinutes(Math.floor(remainMs / 60000));
  if (isPaused) return <span className="text-yellow-600 text-xs font-medium">⏸ {label} (la client)</span>;
  if (isOutsideBusinessHours) return <span className="text-yellow-600 text-xs font-medium">⏸ {label} (în afara programului)</span>;
  const totalMs = limitMinutes * 60000;
  const pct = remainMs / totalMs;
  const color = pct < 0.2 ? 'text-red-600' : pct < 0.4 ? 'text-yellow-600' : 'text-gray-700 dark:text-dark-text';
  return <span className={`${color} text-xs font-medium`}>{label}</span>;
}

function formatMinutes(m: number): string {
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return r > 0 ? `${h}h ${r}min` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}z ${rh}h` : `${d}z`;
}

// ── Column definitions ─────────────────────────────────

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible: boolean;
  render: (t: Ticket) => React.ReactNode;
  filterType?: 'text' | 'select';
  filterOptions?: { value: string; label: string }[];
  getFilterValue?: (t: Ticket) => string;
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'id',
    label: 'ID',
    defaultVisible: true,
    render: (t) => <span className="font-mono text-xs text-gray-400">#{formatTicketId(t.numericId)}</span>,
    filterType: 'text',
    getFilterValue: (t) => formatTicketId(t.numericId),
  },
  {
    key: 'subject',
    label: 'Subiect',
    defaultVisible: true,
    render: (t) => <Link to={`/admin/tickets/${t.id}`} className="font-medium text-primary hover:underline">{t.subject}</Link>,
    filterType: 'text',
    getFilterValue: (t) => t.subject,
  },
  {
    key: 'description',
    label: 'Descriere client',
    defaultVisible: false,
    render: (t) => <span className="text-sm text-gray-500 max-w-xs truncate block" title={t.description ?? undefined}>{t.description || '—'}</span>,
    filterType: 'text',
    getFilterValue: (t) => t.description ?? '',
  },
  {
    key: 'status',
    label: 'Status',
    defaultVisible: true,
    render: (t) => <Badge className={STATUS_COLORS[t.status]}>{STATUS_LABELS[t.status]}</Badge>,
    filterType: 'select',
    filterOptions: [{ value: '', label: 'Toate' }, ...ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))],
    getFilterValue: (t) => t.status,
  },
  {
    key: 'priority',
    label: 'Prioritate',
    defaultVisible: true,
    render: (t) => <Badge className={PRIORITY_COLORS[t.priority]}>{PRIORITY_LABELS[t.priority]}</Badge>,
    filterType: 'select',
    filterOptions: [{ value: '', label: 'Toate' }, ...ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))],
    getFilterValue: (t) => t.priority,
  },
  {
    key: 'createdBy',
    label: 'Reprezentant client',
    defaultVisible: true,
    render: (t) => <span className="text-sm text-gray-600">{t.createdBy?.name ?? '—'}</span>,
    filterType: 'text',
    getFilterValue: (t) => t.createdBy?.name ?? '',
  },
  {
    key: 'createdAt',
    label: 'Creat',
    defaultVisible: true,
    render: (t) => <span className="text-sm text-gray-500">{formatDateTime(t.createdAt)}</span>,
    filterType: 'text',
    getFilterValue: (t) => formatDateTime(t.createdAt),
  },
  {
    key: 'assignedTo',
    label: 'Atribuit',
    defaultVisible: true,
    render: (t) => <span className="text-sm text-gray-500">{t.assignedTo?.name ?? '—'}</span>,
    filterType: 'text',
    getFilterValue: (t) => t.assignedTo?.name ?? '',
  },
  {
    key: 'supervisor',
    label: 'Supervizor',
    defaultVisible: false,
    render: (t) => <span className="text-sm text-gray-500">{t.supervisor?.name ?? '—'}</span>,
    filterType: 'text',
    getFilterValue: (t) => t.supervisor?.name ?? '',
  },
];

// ── Persistence helpers ─────────────────────────────────

const STORAGE_KEY = 'helpdesk_admin_ticket_columns';
const ORDER_STORAGE_KEY = 'helpdesk_admin_ticket_col_order';
const WIDTH_STORAGE_KEY = 'helpdesk_admin_ticket_col_widths';
const STATUS_FILTER_OPTIONS = [{ value: '', label: 'Toate statusurile' }, { value: 'OPEN_GROUP', label: 'Deschise (toate neînchise)' }, ...ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))];

function loadVisibleColumns(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* ignore */ }
  const defaults = new Set(COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key));
  defaults.add('clientName');
  defaults.add('slaResponse');
  defaults.add('slaResolution');
  defaults.add('projectName');
  return defaults;
}

function saveVisibleColumns(cols: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...cols]));
}

function loadColumnOrder(): string[] {
  try {
    const raw = localStorage.getItem(ORDER_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as string[];
  } catch { /* ignore */ }
  return [];
}

function saveColumnOrder(order: string[]) {
  localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order));
}

function loadColumnWidths(): Record<string, number> {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Record<string, number>;
  } catch { /* ignore */ }
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  localStorage.setItem(WIDTH_STORAGE_KEY, JSON.stringify(widths));
}

// ── Component ─────────────────────────────────────────

export function AdminTicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAuthStore((s) => s.user);
  const deleteTicket = useDeleteTicket();
  const [page, setPage] = useState(() => {
    const raw = Number(searchParams.get('page') ?? '1');
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  });
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');
  const [priority, setPriority] = useState(() => searchParams.get('priority') ?? '');
  // "Tichetele mele" preset: assigned to me + in work/estimating (set via ?mine=1)
  const [mine, setMine] = useState(() => searchParams.get('mine') === '1');
  const [visibleCols, setVisibleCols] = useState(loadVisibleColumns);
  const [columnOrder, setColumnOrder] = useState(loadColumnOrder);
  const [showColSettings, setShowColSettings] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [groupByClient, setGroupByClient] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadColumnWidths);

  const { data, isLoading } = useTickets({
    page,
    pageSize: 50,
    search: search || undefined,
    status: mine ? undefined : (status || undefined),
    statusIn: mine ? 'ENCORSA_IN_PROGRESS,ENCORSA_ESTIMATING' : undefined,
    assignedToId: mine ? (currentUser?.id || undefined) : undefined,
    priority: priority || undefined,
  });
  const tickets = data?.data ?? [];
  const pagination = data?.meta.pagination;

  useEffect(() => {
    const nextPage = Number(searchParams.get('page') ?? '1');
    const resolvedPage = Number.isFinite(nextPage) && nextPage > 0 ? nextPage : 1;
    const nextSearch = searchParams.get('search') ?? '';
    const nextStatus = searchParams.get('status') ?? '';
    const nextPriority = searchParams.get('priority') ?? '';
    const nextMine = searchParams.get('mine') === '1';

    setPage((prev) => (prev === resolvedPage ? prev : resolvedPage));
    setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
    setStatus((prev) => (prev === nextStatus ? prev : nextStatus));
    setPriority((prev) => (prev === nextPriority ? prev : nextPriority));
    setMine((prev) => (prev === nextMine ? prev : nextMine));
  }, [searchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (page > 1) nextParams.set('page', String(page));
    if (search) nextParams.set('search', search);
    if (status && !mine) nextParams.set('status', status);
    if (priority) nextParams.set('priority', priority);
    if (mine) nextParams.set('mine', '1');
    setSearchParams(nextParams, { replace: true });
  }, [page, search, status, priority, mine, setSearchParams]);

  const ticketIds = useMemo(() => tickets.map((t) => t.id), [tickets]);
  const { data: slaData } = useTicketSlaBatch(ticketIds);
  const slaMap: Record<string, TicketSla> = slaData?.data ?? {};

  const allColumns = useMemo<ColumnDef[]>(() => {
    const clientCol: ColumnDef = {
      key: 'clientName',
      label: 'Client',
      defaultVisible: true,
      render: (t) => {
        const name = (t as Ticket & { clientName?: string | null }).clientName;
        return <span className="text-sm text-gray-600">{name ?? '—'}</span>;
      },
      filterType: 'text',
      getFilterValue: (t) => (t as Ticket & { clientName?: string | null }).clientName ?? '',
    };
    const slaFilterOptions = [
      { value: '', label: 'Toate' },
      { value: 'respectat', label: 'Respectat' },
      { value: 'depasit', label: 'Depășit' },
      { value: 'justificat', label: 'Justificat' },
      { value: 'in_curs', label: 'În curs' },
      { value: 'fara', label: '—' },
    ];
    function slaFilterValue(deadline: string | null | undefined, met: boolean | null | undefined, justification: string | null | undefined): string {
      if (!deadline) return 'fara';
      if (met === true) return 'respectat';
      if (met === false && justification) return 'justificat';
      if (met === false) return 'depasit';
      return 'in_curs';
    }
    const slaResponseCol: ColumnDef = {
      key: 'slaResponse',
      label: 'Termen răspuns',
      defaultVisible: true,
      render: (t) => {
        const sla = slaMap[t.id];
        if (!sla) return <span className="text-gray-400">—</span>;
        return formatSlaRemaining(sla.responseDeadline, sla.responseMet, sla.isPaused, sla.firstResponseMinutes, sla.responseJustification, sla.isOutsideBusinessHours);
      },
      filterType: 'select',
      filterOptions: slaFilterOptions,
      getFilterValue: (t) => {
        const sla = slaMap[t.id];
        return slaFilterValue(sla?.responseDeadline, sla?.responseMet, sla?.responseJustification);
      },
    };
    const slaResolutionCol: ColumnDef = {
      key: 'slaResolution',
      label: 'Termen rezolvare',
      defaultVisible: true,
      render: (t) => {
        const sla = slaMap[t.id];
        if (!sla) return <span className="text-gray-400">—</span>;
        return formatSlaRemaining(sla.resolutionDeadline, sla.resolutionMet, sla.isPaused, sla.resolutionMinutes, sla.resolutionJustification, sla.isOutsideBusinessHours);
      },
      filterType: 'select',
      filterOptions: slaFilterOptions,
      getFilterValue: (t) => {
        const sla = slaMap[t.id];
        return slaFilterValue(sla?.resolutionDeadline, sla?.resolutionMet, sla?.resolutionJustification);
      },
    };
    const idx = COLUMNS.findIndex((c) => c.key === 'createdBy');
    const result = [...COLUMNS];
    result.splice(idx, 0, clientCol);

    // "La mine" column
    const laMineCol: ColumnDef = {
      key: 'laMine',
      label: 'La mine',
      defaultVisible: true,
      render: (t) => {
        if (!currentUser) return null;
        const isAssigned = currentUser.id === t.assignedToId;
        const isSupervisor = currentUser.id === t.supervisorId;
        const isAdmin = currentUser.role === 'ADMIN';
        const mine =
          (isAssigned && ['ENCORSA_ESTIMATING', 'ENCORSA_IN_PROGRESS'].includes(t.status)) ||
          (isSupervisor && t.status === 'ENCORSA_SUPERVISOR_REVIEW') ||
          (isAdmin && t.status === 'ENCORSA_ASSIGNING');
        return mine ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">La mine</Badge> : null;
      },
      filterType: 'select',
      filterOptions: [{ value: '', label: 'Toate' }, { value: 'da', label: 'La mine' }],
      getFilterValue: (t) => {
        if (!currentUser) return '';
        const isAssigned = currentUser.id === t.assignedToId;
        const isSupervisor = currentUser.id === t.supervisorId;
        const isAdmin = currentUser.role === 'ADMIN';
        const mine =
          (isAssigned && ['ENCORSA_ESTIMATING', 'ENCORSA_IN_PROGRESS'].includes(t.status)) ||
          (isSupervisor && t.status === 'ENCORSA_SUPERVISOR_REVIEW') ||
          (isAdmin && t.status === 'ENCORSA_ASSIGNING');
        return mine ? 'da' : '';
      },
    };
    result.splice(1, 0, laMineCol); // After ID column

    // "Durata dezvoltare" column
    const devDurationCol: ColumnDef = {
      key: 'developmentDuration',
      label: 'Durata dezvoltare (ore)',
      defaultVisible: false,
      render: (t) => <span className="text-sm text-gray-500">{t.developmentDuration != null ? `${t.developmentDuration}h` : '—'}</span>,
      filterType: 'text',
      getFilterValue: (t) => t.developmentDuration != null ? String(t.developmentDuration) : '',
    };

    // "Descriere dezvoltare" column
    const devDescriptionCol: ColumnDef = {
      key: 'developmentDescription',
      label: 'Descriere dezvoltare',
      defaultVisible: false,
      render: (t) => <span className="text-sm text-gray-500 max-w-xs truncate block">{t.developmentDescription ?? '—'}</span>,
      filterType: 'text',
      getFilterValue: (t) => t.developmentDescription ?? '',
    };

    // "Data deschidere" column (createdAt)
    const openDateCol: ColumnDef = {
      key: 'openDate',
      label: 'Data deschidere',
      defaultVisible: false,
      render: (t) => <span className="text-sm text-gray-500">{formatDateTime(t.createdAt)}</span>,
      filterType: 'text',
      getFilterValue: (t) => formatDateTime(t.createdAt),
    };

    // "Data închidere" column (resolvedAt)
    const closeDateCol: ColumnDef = {
      key: 'closeDate',
      label: 'Data închidere',
      defaultVisible: false,
      render: (t) => <span className="text-sm text-gray-500">{t.resolvedAt ? formatDateTime(t.resolvedAt) : '—'}</span>,
      filterType: 'text',
      getFilterValue: (t) => t.resolvedAt ? formatDateTime(t.resolvedAt) : '',
    };

    // "Proiect / Subcategorie" column
    const projectCol: ColumnDef = {
      key: 'projectName',
      label: 'Proiect / Subcategorie',
      defaultVisible: true,
      render: (t) => {
        const name = (t as Ticket & { projectName?: string | null }).projectName;
        return <span className="text-sm text-gray-600">{name ?? '—'}</span>;
      },
      filterType: 'text',
      getFilterValue: (t) => (t as Ticket & { projectName?: string | null }).projectName ?? '',
    };

    result.push(slaResponseCol, slaResolutionCol, projectCol, devDurationCol, devDescriptionCol, openDateCol, closeDateCol);

    // "Rating" column (CSAT score)
    const ratingCol: ColumnDef = {
      key: 'rating',
      label: 'Rating',
      defaultVisible: false,
      render: (t) => {
        const csat = t.csatRatings?.[0];
        if (!csat) return <span className="text-gray-400">—</span>;
        return (
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} size={12} className={s <= csat.score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'} />
            ))}
          </div>
        );
      },
      filterType: 'select',
      filterOptions: [
        { value: '', label: 'Toate' },
        { value: '1', label: '1 stea' },
        { value: '2', label: '2 stele' },
        { value: '3', label: '3 stele' },
        { value: '4', label: '4 stele' },
        { value: '5', label: '5 stele' },
        { value: 'fara', label: '—' },
      ],
      getFilterValue: (t) => {
        const csat = t.csatRatings?.[0];
        return csat ? String(csat.score) : 'fara';
      },
    };
    result.push(ratingCol);

    // Admin-only delete action column
    if (currentUser?.role === 'ADMIN') {
      result.push({
        key: 'actions',
        label: 'Acțiuni',
        defaultVisible: true,
        render: (t) => (
          <button
            type="button"
            title="Șterge tichetul"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Sigur vrei să ștergi tichetul #${formatTicketId(t.numericId)}?`)) {
                deleteTicket.mutate(t.id);
              }
            }}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
          >
            <Trash2 size={16} />
          </button>
        ),
      });
    }

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slaMap, currentUser]);

  const toggleCol = useCallback((key: string) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveVisibleColumns(next);
      return next;
    });
  }, []);

  // ── Column drag-and-drop reorder ─────────────────────
  const dragColRef = useRef<string | null>(null);

  const handleDragStart = useCallback((key: string) => {
    dragColRef.current = key;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((targetKey: string) => {
    const sourceKey = dragColRef.current;
    dragColRef.current = null;
    if (!sourceKey || sourceKey === targetKey) return;

    // Build current order from activeCols (which already respects saved order)
    setColumnOrder((prevOrder) => {
      // Get all column keys in current display order
      const visible = allColumns.filter((c) => visibleCols.has(c.key));
      let keys: string[];
      if (prevOrder.length > 0) {
        const orderMap = new Map(prevOrder.map((k, i) => [k, i]));
        keys = [...visible].sort((a, b) => (orderMap.get(a.key) ?? 9999) - (orderMap.get(b.key) ?? 9999)).map((c) => c.key);
      } else {
        keys = visible.map((c) => c.key);
      }
      // Also include keys of hidden columns to preserve their position
      const allKeys = allColumns.map((c) => c.key);
      const fullOrder = keys.length > 0 ? [...keys, ...allKeys.filter((k) => !keys.includes(k))] : allKeys;

      const srcIdx = fullOrder.indexOf(sourceKey);
      const tgtIdx = fullOrder.indexOf(targetKey);
      if (srcIdx === -1 || tgtIdx === -1) return prevOrder;

      const newOrder = [...fullOrder];
      newOrder.splice(srcIdx, 1);
      newOrder.splice(tgtIdx, 0, sourceKey);
      saveColumnOrder(newOrder);
      return newOrder;
    });
  }, [allColumns, visibleCols]);

  // Reorder in the settings panel via drag
  const settingsDragRef = useRef<string | null>(null);
  const handleSettingsDrop = useCallback((targetKey: string) => {
    const sourceKey = settingsDragRef.current;
    settingsDragRef.current = null;
    if (!sourceKey || sourceKey === targetKey) return;

    setColumnOrder((prevOrder) => {
      const allKeys = allColumns.map((c) => c.key);
      const fullOrder = prevOrder.length > 0 ? [...prevOrder, ...allKeys.filter((k) => !prevOrder.includes(k))] : allKeys;
      const srcIdx = fullOrder.indexOf(sourceKey);
      const tgtIdx = fullOrder.indexOf(targetKey);
      if (srcIdx === -1 || tgtIdx === -1) return prevOrder;
      const newOrder = [...fullOrder];
      newOrder.splice(srcIdx, 1);
      newOrder.splice(tgtIdx, 0, sourceKey);
      saveColumnOrder(newOrder);
      return newOrder;
    });
  }, [allColumns]);

  // ── Column resize ─────────────────────────────────────
  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null);

  const handleResizeStart = useCallback((key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest('th');
    const startW = th ? th.getBoundingClientRect().width : 120;
    resizeRef.current = { key, startX: e.clientX, startW };

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const diff = ev.clientX - resizeRef.current.startX;
      const newWidth = Math.max(50, resizeRef.current.startW + diff);
      setColumnWidths((prev) => {
        const next = { ...prev, [resizeRef.current!.key]: newWidth };
        return next;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist after resize ends
      setColumnWidths((prev) => {
        saveColumnWidths(prev);
        return prev;
      });
      resizeRef.current = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const activeCols = useMemo(() => {
    const visible = allColumns.filter((c) => visibleCols.has(c.key));
    if (columnOrder.length === 0) return visible;
    const orderMap = new Map(columnOrder.map((key, idx) => [key, idx]));
    return [...visible].sort((a, b) => {
      const ai = orderMap.get(a.key) ?? 9999;
      const bi = orderMap.get(b.key) ?? 9999;
      return ai - bi;
    });
  }, [visibleCols, allColumns, columnOrder]);
  const colCount = activeCols.length;

  // Client-side column filter
  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      for (const col of activeCols) {
        const filterVal = columnFilters[col.key];
        if (!filterVal) continue;
        const cellVal = col.getFilterValue?.(t) ?? '';
        if (col.filterType === 'select') {
          if (cellVal !== filterVal) return false;
        } else {
          if (!cellVal.toLowerCase().includes(filterVal.toLowerCase())) return false;
        }
      }
      return true;
    });
  }, [tickets, columnFilters, activeCols]);

  // Grouping by client
  const groups = useMemo(() => {
    if (!groupByClient) return null;
    const map = new Map<string, Ticket[]>();
    for (const t of filteredTickets) {
      const key = (t as Ticket & { clientName?: string | null }).clientName ?? t.createdBy?.name ?? '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [groupByClient, filteredTickets]);

  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }, []);

  const setColFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // Always use server-side Excel export — pass column filters as query params
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (priority) params.set('priority', priority);
      // Forward column-level text filters to server
      const filterMapping: Record<string, string> = {
        id: 'filterID', subject: 'filterSubject', status: 'filterStatus',
        priority: 'filterPriority', clientName: 'filterClient', createdBy: 'filterCreatedBy',
        assignedTo: 'filterAssignedTo', supervisor: 'filterSupervisor', projectName: 'filterProject',
      };
      for (const [colKey, filterVal] of Object.entries(columnFilters)) {
        if (filterVal && filterMapping[colKey]) {
          params.set(filterMapping[colKey], filterVal);
        }
      }
      const q = params.toString();
      const res = await fetch(`/api/tickets/export${q ? `?${q}` : ''}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tichete-export.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  }, [search, status, priority, columnFilters]);

  const renderRow = (t: Ticket) => (
    <Tr key={t.id}>
      {activeCols.map((col) => (
        <Td key={col.key} style={columnWidths[col.key] ? { width: columnWidths[col.key], minWidth: columnWidths[col.key], maxWidth: columnWidths[col.key] } : undefined}>{col.render(t)}</Td>
      ))}
    </Tr>
  );

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      <div className="flex items-center justify-between shrink-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">{mine ? 'Tichetele mele' : 'Toate tichetele'}</h1>
        <div className="flex items-center gap-2">
          <Link to="/admin/tickets/new">
            <Button size="sm">+ Tichet Nou</Button>
          </Link>
          <Button variant="outline" size="sm" disabled={exporting} onClick={handleExport}>
            <Download size={16} /> {exporting ? 'Export...' : 'Export Excel'}
          </Button>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-dark-muted">
            <input type="checkbox" checked={groupByClient} onChange={(e) => setGroupByClient(e.target.checked)} className="rounded" />
            Grupare client
          </label>
          <div className="relative">
            <Button variant="outline" size="sm" onClick={() => setShowColSettings(!showColSettings)}>
              <Settings2 size={16} /> Coloane
            </Button>
            {showColSettings && (
              <div className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border bg-white p-2 shadow-lg dark:border-dark-border dark:bg-dark-card max-h-80 overflow-y-auto">
                <p className="px-2 pb-1 text-xs text-gray-400 dark:text-dark-muted">Trage pentru a reordona</p>
                {(() => {
                  // Sort allColumns by saved order for the settings panel
                  const sorted = columnOrder.length > 0
                    ? [...allColumns].sort((a, b) => {
                        const orderMap = new Map(columnOrder.map((k, i) => [k, i]));
                        return (orderMap.get(a.key) ?? 9999) - (orderMap.get(b.key) ?? 9999);
                      })
                    : allColumns;
                  return sorted.map((c) => (
                    <div
                      key={c.key}
                      draggable
                      onDragStart={() => { settingsDragRef.current = c.key; }}
                      onDragOver={handleDragOver}
                      onDrop={() => handleSettingsDrop(c.key)}
                      className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50 cursor-grab active:cursor-grabbing dark:hover:bg-dark-border dark:text-dark-text"
                    >
                      <GripVertical size={14} className="text-gray-300 dark:text-dark-muted shrink-0" />
                      <label className="flex items-center gap-2 flex-1 cursor-pointer">
                        <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => toggleCol(c.key)} className="rounded" />
                        {c.label}
                      </label>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {mine && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          <span>
            Doar tichetele alocate ție, în lucru sau în estimare.
            {!isLoading && tickets.length === 0 && ' Nu ai tichete în lucru în momentul de față.'}
          </span>
          <button
            type="button"
            onClick={() => { setMine(false); setPage(1); }}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
          >
            <X size={14} /> Vezi toate tichetele
          </button>
        </div>
      )}

      <Card className="p-4 shrink-0">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input placeholder="Caută..." className="pl-9" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <Select
            options={STATUS_FILTER_OPTIONS}
            value={mine ? '' : status}
            disabled={mine}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          />
          <Select
            options={[{ value: '', label: 'Toate prioritățile' }, ...ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))]}
            value={priority}
            onChange={(e) => { setPriority(e.target.value); setPage(1); }}
          />
        </div>
      </Card>

      <Card className="flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-auto thick-scrollbar">
        <Table>
          <Thead>
            <Tr>
              {activeCols.map((col) => (
                <Th
                  key={col.key}
                  draggable
                  onDragStart={() => handleDragStart(col.key)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(col.key)}
                  className="cursor-grab active:cursor-grabbing select-none relative"
                  style={columnWidths[col.key] ? { width: columnWidths[col.key], minWidth: columnWidths[col.key], maxWidth: columnWidths[col.key] } : undefined}
                >
                  {col.label}
                  {/* Resize handle */}
                  <span
                    onMouseDown={(e) => handleResizeStart(col.key, e)}
                    className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-400/40"
                    onClick={(e) => e.stopPropagation()}
                    draggable={false}
                  />
                </Th>
              ))}
            </Tr>
            {/* Per-column filters */}
            <Tr>
              {activeCols.map((col) => (
                <Th key={col.key} className="py-1" style={columnWidths[col.key] ? { width: columnWidths[col.key], minWidth: columnWidths[col.key], maxWidth: columnWidths[col.key] } : undefined}>
                  {col.filterType === 'select' && col.filterOptions ? (
                    <select
                      value={columnFilters[col.key] ?? ''}
                      onChange={(e) => setColFilter(col.key, e.target.value)}
                      className="w-full rounded border border-gray-200 px-1.5 py-1 text-xs font-normal text-gray-600"
                    >
                      {col.filterOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : col.filterType === 'text' ? (
                    <input
                      type="text"
                      placeholder="Filtrare..."
                      value={columnFilters[col.key] ?? ''}
                      onChange={(e) => setColFilter(col.key, e.target.value)}
                      className="w-full rounded border border-gray-200 px-1.5 py-1 text-xs font-normal text-gray-600 placeholder:text-gray-300"
                    />
                  ) : null}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => (
              <Tr key={i}><Td colSpan={colCount}><Skeleton className="h-4 w-full" /></Td></Tr>
            ))}
            {!isLoading && filteredTickets.length === 0 && (
              <Tr><Td colSpan={colCount} className="text-center text-gray-400">Nu s-au găsit tichete</Td></Tr>
            )}
            {!isLoading && !groupByClient && filteredTickets.map(renderRow)}
            {!isLoading && groupByClient && groups?.map(([clientName, clientTickets]) => (
              <GroupRows
                key={clientName}
                name={clientName}
                tickets={clientTickets}
                colCount={colCount}
                collapsed={collapsedGroups.has(clientName)}
                onToggle={() => toggleGroup(clientName)}
                renderRow={renderRow}
              />
            ))}
          </Tbody>
        </Table>
        </div>
        {pagination && <Pagination page={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />}
      </Card>
    </div>
  );
}

function GroupRows({ name, tickets, colCount, collapsed, onToggle, renderRow }: {
  name: string;
  tickets: Ticket[];
  colCount: number;
  collapsed: boolean;
  onToggle: () => void;
  renderRow: (t: Ticket) => React.ReactNode;
}) {
  return (
    <>
      <Tr>
        <Td colSpan={colCount} className="bg-gray-50">
          <button type="button" onClick={onToggle} className="flex items-center gap-1 text-sm font-semibold text-gray-700">
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            {name} <span className="ml-1 font-normal text-gray-400">({tickets.length})</span>
          </button>
        </Td>
      </Tr>
      {!collapsed && tickets.map(renderRow)}
    </>
  );
}
